import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FiFileText, FiDownload } from "react-icons/fi";
import PageHeader from "../../components/common/pageheader/PageHeader";
import Loader from "../../components/common/Loader/Loader";
import { getPaymentTransactions } from "../../services/paymentsApi";
import { useAuth } from "../../context/AuthContext";
import { formatDateTime } from "../../utils/formatDateTime";
import { translatePaymentFailure } from "../../utils/paymentFailure";
import { downloadInvoicePdf } from "../../utils/invoicePdf";
import styles from "./InvoicesPage.module.css";

const STATUS_CLASS = {
  SUCCEEDED: styles.statusSucceeded,
  FAILED: styles.statusFailed,
  PENDING: styles.statusPending,
  REFUNDED: styles.statusRefunded,
};

const InvoicesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getPaymentTransactions();
        if (active) setTransactions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching payment transactions:", err);
        if (active) {
          setTransactions([]);
          toast.error(t("pages.invoices.loadError"), { toastId: "invoices-fetch-error" });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [t]);

  const statusLabel = (status) =>
    t(`pages.invoices.status.${status}`, { defaultValue: status });
  const purposeLabel = (purpose) =>
    t(`pages.invoices.purpose.${purpose}`, { defaultValue: purpose });

  const formatAmount = (amount, currency) => {
    const n = Number(amount);
    const value = Number.isFinite(n) ? n.toFixed(2) : amount;
    return `${value} ${currency || ""}`.trim();
  };

  const handleDownload = async (tx) => {
    if (downloadingId || tx.status !== "SUCCEEDED") return;
    setDownloadingId(tx.id);
    try {
      await downloadInvoicePdf({ tx, tenantName: user?.tenantName, t });
    } catch (err) {
      console.error("Invoice PDF generation failed:", err);
      toast.error(t("pages.invoices.pdf.error"));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div>
      <PageHeader title={t("pages.invoices.title")} />

      {loading && <Loader fullscreen />}

      {!loading && transactions.length === 0 && (
        <div className={styles.emptyState}>
          <FiFileText className={styles.emptyIcon} />
          <p className={styles.emptyText}>{t("pages.invoices.empty")}</p>
        </div>
      )}

      {!loading && transactions.length > 0 && (
        <div className={styles.card}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("pages.invoices.colDate")}</th>
                  <th>{t("pages.invoices.colDescription")}</th>
                  <th>{t("pages.invoices.colReference")}</th>
                  <th className={styles.amountCol}>{t("pages.invoices.colAmount")}</th>
                  <th>{t("pages.invoices.colStatus")}</th>
                  <th className={styles.actionsCol} aria-label={t("pages.invoices.pdf.download")}></th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className={styles.dateCell}>{formatDateTime(tx.createdAt)}</td>
                    <td>
                      <div className={styles.itemName}>
                        {tx.itemName || purposeLabel(tx.purpose)}
                      </div>
                      <div className={styles.purposeSub}>{purposeLabel(tx.purpose)}</div>
                      {tx.status === "FAILED" && (
                        <div className={styles.failureNote}>
                          {translatePaymentFailure(t, tx.failureMessage)}
                        </div>
                      )}
                    </td>
                    <td className={styles.referenceCell}>{tx.gatewayReference || "—"}</td>
                    <td className={styles.amountCol}>
                      {formatAmount(tx.amount, tx.currency)}
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${STATUS_CLASS[tx.status] || ""}`}
                        title={tx.status === "PENDING" ? t("pages.invoices.pendingHint") : undefined}
                      >
                        {statusLabel(tx.status)}
                      </span>
                    </td>
                    <td className={styles.actionsCol}>
                      {tx.status === "SUCCEEDED" && (
                        <button
                          type="button"
                          className={styles.downloadBtn}
                          onClick={() => handleDownload(tx)}
                          disabled={downloadingId === tx.id}
                          title={t("pages.invoices.pdf.download")}
                          aria-label={t("pages.invoices.pdf.download")}
                        >
                          <FiDownload />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
